/**
 * ndg/shared-slider — module partagé (WordPress Script Modules API).
 *
 * Compilé une seule fois (build/shared/slider.js), enregistré côté PHP via
 * wp_register_script_module('ndg/shared-slider', ...) et déclaré comme
 * dépendance externe (voir bin/webpack.config.js, requestToExternalModule)
 * par chaque bloc qui l'importe : films-slider, events-slider, riders
 * partagent donc un seul téléchargement/cache — pas une copie par bloc.
 *
 * Regroupe toute la logique Embla commune : gestes trackpad/molette,
 * garde anti-clic-après-drag, navigation clavier, flèches prev/next,
 * accessibilité (role carousel, live region, inert sur slides hors-champ),
 * respect de prefers-reduced-motion.
 */

import EmblaCarousel from 'embla-carousel';
import { WheelGesturesPlugin } from 'embla-carousel-wheel-gestures';

function prefersReducedMotion() {
	return window.matchMedia( '(prefers-reduced-motion: reduce)' ).matches;
}

function reducedMotionDuration( defaultDuration = 25 ) {
	return prefersReducedMotion() ? 0 : defaultDuration;
}

function wireDragGuard( embla, ref ) {
	let wasDragging = false;
	embla.on( 'pointerDown', () => {
		wasDragging = false;
		ref.classList.add( 'is-dragging' );
	} );
	embla.on( 'scroll', () => { wasDragging = true; } );
	embla.on( 'pointerUp', () => ref.classList.remove( 'is-dragging' ) );

	// Capture : intercepte avant les listeners des slides (liens, boutons…)
	// pour éviter qu'un drag se termine en navigation/clic non voulu.
	ref.addEventListener( 'click', ( e ) => {
		if ( wasDragging ) {
			e.preventDefault();
			e.stopPropagation();
			wasDragging = false;
		}
	}, true );
}

function wireKeyboard( embla, ref ) {
	ref.setAttribute( 'tabindex', '0' );
	ref.addEventListener( 'keydown', ( e ) => {
		if ( e.key === 'ArrowRight' ) embla.scrollNext();
		if ( e.key === 'ArrowLeft' )  embla.scrollPrev();
	} );
}

function wireArrows( embla, wrapper ) {
	const prevBtn = wrapper?.querySelector( '[data-dir="prev"]' );
	const nextBtn = wrapper?.querySelector( '[data-dir="next"]' );
	if ( ! prevBtn || ! nextBtn ) return;

	const updateArrows = () => {
		prevBtn.disabled = ! embla.canScrollPrev();
		nextBtn.disabled = ! embla.canScrollNext();
	};
	prevBtn.addEventListener( 'click', () => embla.scrollPrev() );
	nextBtn.addEventListener( 'click', () => embla.scrollNext() );
	embla.on( 'select', updateArrows );
	embla.on( 'init', updateArrows );
	updateArrows();
}

function wireA11y( embla, { root, label } ) {
	if ( ! root ) return;

	root.setAttribute( 'role', 'region' );
	root.setAttribute( 'aria-roledescription', 'carousel' );
	if ( label ) root.setAttribute( 'aria-label', label );

	const live = document.createElement( 'div' );
	live.className = 'ndg-slider__live';
	live.setAttribute( 'aria-live', 'polite' );
	live.setAttribute( 'aria-atomic', 'true' );
	root.appendChild( live );

	const updateInert = () => {
		const inView = new Set( embla.slidesInView() );
		embla.slideNodes().forEach( ( slide, i ) => {
			slide.inert = ! inView.has( i );
		} );
	};

	const announce = () => {
		const slides = embla.slideNodes();
		live.textContent = `Diapositive ${ embla.selectedScrollSnap() + 1 } sur ${ slides.length }`;
	};

	embla.on( 'slidesInView', updateInert );
	embla.on( 'select',       announce );
	embla.on( 'reInit',       updateInert );
	updateInert();
}

/**
 * Initialise un carrousel Embla complet (gestes, clavier, flèches, a11y).
 *
 * @param {HTMLElement} ref Élément viewport Embla (contient directement le track).
 * @param {Object}  options
 * @param {string}  [options.wrapperSelector] Sélecteur du conteneur englobant
 *   (header + flèches + viewport) utilisé pour trouver les flèches et le
 *   libellé, et comme cible du rôle "carousel". Défaut : parent direct de ref.
 * @param {string}  [options.labelSelector] Sélecteur (relatif au wrapper) du
 *   titre à utiliser comme aria-label du carrousel.
 * @param {string}  [options.fallbackLabel] Libellé si labelSelector ne matche rien.
 * @param {boolean} [options.dragFree=false] Défilement libre (sans snap) ou non.
 * @param {boolean} [options.watchResize=false] Recalcul auto au resize (Embla).
 * @param {Object}  [options.emblaOptions] Options Embla additionnelles/override.
 * @returns {import('embla-carousel').EmblaCarouselType}
 */
export function initSlider( ref, {
	wrapperSelector = null,
	labelSelector   = null,
	fallbackLabel   = '',
	dragFree        = false,
	watchResize     = false,
	emblaOptions    = {},
} = {} ) {
	const embla = EmblaCarousel( ref, {
		loop:          false,
		align:         'start',
		dragFree,
		containScroll: 'trimSnaps',
		watchResize,
		duration:      reducedMotionDuration(),
		...emblaOptions,
	}, [ WheelGesturesPlugin( { forceWheelAxis: 'x' } ) ] );

	wireDragGuard( embla, ref );
	wireKeyboard( embla, ref );

	const wrapper = wrapperSelector ? ref.closest( wrapperSelector ) : ref.parentElement;
	wireArrows( embla, wrapper );

	const label = labelSelector
		? wrapper?.querySelector( labelSelector )?.textContent?.trim()
		: '';
	wireA11y( embla, { root: wrapper, label: label || fallbackLabel } );

	return embla;
}
